import React, { useState, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';

/**
 * Free-form tags with autocomplete (D7).
 *
 * Planning/TME-WIKI-2026-09-02.md §4.4, §6.4, §10.1.
 *
 * ⚠️ THE NORMALISATION HERE MUST AGREE WITH THE SERVER'S, because the server
 * KEYS ON IT: a membership row's sort key is built from the normalised tag, and
 * the registry row's key IS the normalised tag. The definition lives in
 * `lambda/layer/nodejs/tmeWikiRead.js` as `normaliseName` - the same function
 * wiki names use - and this is its frontend twin. It is a CONVENIENCE, never the
 * authority: the server normalises again on every write, so a disagreement here
 * shows a duplicate chip rather than storing a bad row.
 *
 * ⚠️ CASE- AND SPACE-INSENSITIVE, TYPED CASING KEPT. `Intune`, `intune` and
 * ` Intune ` are one tag and the first spelling is the one displayed; `InTune`
 * and `In Tune` remain TWO tags, because collapsing them would merge things
 * nobody said were the same.
 */

export function normaliseTag(value) {
    return String(value == null ? '' : value).trim().toLowerCase().replace(/\s+/g, ' ');
}

/** The server's own bounds (§6.4), checked on the NORMALISED value. */
export const MAX_TAGS = 25;
export const MAX_TAG_LENGTH = 50;

const TagInput = ({
    value = [],
    onChange,
    suggestions = [],
    disabled = false,
    placeholder = 'Add a tag…'
}) => {
    const [text, setText] = useState('');
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    const keys = value.map(normaliseTag);

    const add = (raw) => {
        const display = String(raw || '').trim();
        const key = normaliseTag(display);

        if (!key) {
            // Empty AFTER normalisation: the server refuses it, because it would
            // write a membership row with an empty sort key.
            setError(null);
            setText('');
            return;
        }
        if (key.length > MAX_TAG_LENGTH) {
            setError(`A tag must be ${MAX_TAG_LENGTH} characters or fewer`);
            return;
        }
        if (keys.includes(key)) {
            // Not an error - the tag is already there under some spelling, and
            // the first spelling wins.
            setError(null);
            setText('');
            return;
        }
        if (value.length >= MAX_TAGS) {
            setError(`A page may carry at most ${MAX_TAGS} tags`);
            return;
        }

        setError(null);
        setText('');
        onChange([...value, display]);
    };

    const removeAt = (index) => {
        setError(null);
        onChange(value.filter((_, i) => i !== index));
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add(text);
            return;
        }
        if (e.key === 'Backspace' && text === '' && value.length > 0) {
            removeAt(value.length - 1);
        }
    };

    const typed = normaliseTag(text);
    const offered = typed
        ? suggestions
            .filter(s => s.tag.includes(typed) && !keys.includes(s.tag))
            .slice(0, 8)
        : [];

    return (
        <div>
            <div className={`flex flex-wrap items-center gap-1 border border-gray-300 rounded px-2 py-1 ${disabled ? 'bg-gray-100' : 'bg-white'}`}>
                {value.map((tag, index) => (
                    <span
                        key={normaliseTag(tag)}
                        className="inline-flex items-center gap-1 rounded bg-primary-50 px-2 py-0.5 text-xs text-primary-800"
                    >
                        {tag}
                        {!disabled && (
                            <button
                                type="button"
                                aria-label={`Remove ${tag}`}
                                onClick={() => removeAt(index)}
                                className="text-primary-500 hover:text-primary-800"
                            >
                                <FaTimes className="w-2 h-2" />
                            </button>
                        )}
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    aria-label="Add a tag"
                    className="flex-1 min-w-[8rem] py-0.5 text-sm outline-none disabled:bg-gray-100"
                    placeholder={placeholder}
                    value={text}
                    disabled={disabled}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={onKeyDown}
                    onBlur={() => add(text)}
                />
            </div>

            {offered.length > 0 && (
                <ul className="mt-1 border border-gray-200 rounded bg-white text-sm shadow-sm">
                    {offered.map(suggestion => (
                        <li key={suggestion.tag}>
                            <button
                                type="button"
                                className="w-full text-left px-2 py-1 hover:bg-gray-100 flex justify-between"
                                // onMouseDown, not onClick: the input's onBlur
                                // fires first on a click and would add the typed
                                // text instead of the suggestion.
                                onMouseDown={(e) => { e.preventDefault(); add(suggestion.tagDisplay || suggestion.tag); }}
                            >
                                <span>{suggestion.tagDisplay || suggestion.tag}</span>
                                <span className="text-gray-400">{suggestion.pageCount}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>
    );
};

export default TagInput;
